export type SolveError = {
  ID: string;
  Label?: string;
  Summary?: number;
  Description?: string;
  Link?: string;
};

/**
 * HTTP Error Response Data from FOND API
 */
export interface HTTPErrorResponse {
  code: string;
  message: string;
}

/** HTTP Errors as thrown by RTK with a data attribute
 *
 * See {@link https://redux-toolkit.js.org/rtk-query/usage-with-typescript#type-safe-error-handling|rtk-query#FetchBaseQueryError}
 * for more details.
 */
export type BasicRtkHttpError = {
  status: number;
  data: HTTPErrorResponse;
};

/**
 * When an error is caught from an RTK call this function can be used to determine
 * if it is an basic HTTPError to narrow the type for further processing.
 *
 * @param error - a thrown error object
 */
export const isBasicRtkHttpError = (error: unknown): error is BasicRtkHttpError => {
  if (error && typeof error === "object" && "data" in error && isHttpErrorResponse(error.data)) {
    return true;
  }
  return false;
};

const isHttpErrorResponse = (errorData: unknown): errorData is HTTPErrorResponse => {
  return typeof errorData === "object" && errorData !== null && "code" in errorData && "message" in errorData;
};

/**
 * Checks if an error is a network-related failure (connection lost, timeout, offline).
 * Network errors should be treated differently from HTTP errors (404, 403) because
 * they indicate temporary connectivity issues rather than missing/unauthorized resources.
 *
 * Handles various network error scenarios:
 * - Connection lost (ERR_INTERNET_DISCONNECTED)
 * - Network changed (ERR_NETWORK_CHANGED - switching WiFi/cellular)
 * - Connection timeout
 * - DNS resolution failures
 * - CORS errors
 *
 * @param error - Error object from RTK Query hook
 * @returns true if this is a network failure that should show a retry dialog
 *
 * @example
 * ```tsx
 * const { error } = useGetProjectQuery(projectId);
 * if (error && isNetworkError(error)) {
 *   return <NetworkErrorModal onReload={refetch} />;
 * }
 * ```
 */
export const isNetworkError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;

  // Check for RTK Query network errors
  if ("status" in error) {
    const { status } = error as { status: unknown };
    // FETCH_ERROR: Failed to fetch (network down, CORS, ERR_NETWORK_CHANGED, etc.)
    // TIMEOUT_ERROR: Request timeout
    return status === "FETCH_ERROR" || status === "TIMEOUT_ERROR";
  }

  // SerializedError with specific network-related error names
  if ("name" in error) {
    const { name } = error as { name?: string };
    const networkErrorNames = ["NetworkError", "TypeError", "AbortError"];
    if (networkErrorNames.includes(name || "")) return true;
  }

  // Check error message for specific network error codes (Chrome error codes)
  if ("message" in error) {
    const { message } = error as { message?: string };
    if (typeof message === "string") {
      const networkErrorPatterns = [
        "ERR_NETWORK_CHANGED", // Network switched (WiFi -> Cellular, etc.)
        "ERR_INTERNET_DISCONNECTED", // Connection lost
        "ERR_CONNECTION_REFUSED", // Server not reachable
        "ERR_CONNECTION_RESET", // Connection dropped
        "ERR_CONNECTION_TIMED_OUT", // Timeout
        "ERR_NAME_NOT_RESOLVED", // DNS failure
        "Failed to fetch", // Generic fetch failure
        "Network request failed", // Generic network failure
      ];

      return networkErrorPatterns.some((pattern) => message.includes(pattern));
    }
  }

  return false;
};
